// Shared primitives for the Snapshot route: design tokens resolved from the
// MUI theme, compact number formatting, delta chips, deep-link values, and
// the card/section chrome the artboards share between the hub-and-spoke and
// flat layouts.
import { ReactNode } from "react";
import { useTheme } from "@mui/material/styles";
import { useGuardedNavigation } from "../../hooks/use_guarded_navigation";
import { SnapshotMetric, SnapshotUnit } from "./types";

export type SnapshotTokens = {
  blue: string;
  blueDeep: string;
  blueSoft: string;
  ink: string;
  ink2: string;
  ink3: string;
  ink4: string;
  ink5: string;
  line: string;
  line2: string;
  surface: string;
  surface2: string;
  surface3: string;
  green1: string;
  greenInk: string;
  red1: string;
  redInk: string;
  orange1: string;
  orangeInk: string;
};

export const useSnapshotTokens = (): SnapshotTokens => {
  const theme = useTheme();
  const a = theme.palette.atomic;
  return {
    blue: a.brandBlue,
    blueDeep: a.brandBlueDark,
    blueSoft: a.blue1,
    ink: a.ink,
    ink2: "#2E3033",
    ink3: a.inkSecondary,
    ink4: a.muted,
    ink5: a.faint,
    line: a.border,
    line2: a.borderLight,
    surface: "#FFFFFF",
    surface2: a.surfaceTint,
    surface3: a.panelTint,
    green1: a.green1,
    greenInk: a.inflowInk,
    red1: a.red1,
    redInk: a.outflowInk,
    orange1: a.orange1,
    orangeInk: "#8A6300",
  };
};

export const EM_DASH = "—";
/** U+2212. The only minus glyph this view emits. */
export const MINUS = "−";

/**
 * The one signed-number formatter. Clamps magnitudes under 0.05 to a plain
 * "0.0" (so a −0.023 never renders as "-0.0") and always uses a true minus
 * glyph. Every signed figure on this page routes through here.
 */
export const fmtSigned = (value: number, digits = 1, suffix = ""): string => {
  const rounded = Number(value.toFixed(digits));
  if (Math.abs(value) < 0.5 * Math.pow(10, -digits) || rounded === 0) {
    return (0).toFixed(digits) + suffix;
  }
  const body = Math.abs(rounded).toFixed(digits);
  return (rounded < 0 ? MINUS : "+") + body + suffix;
};

/** Channel colors: one warm-green-to-olive ramp, so the split reads as a
 *  subdivision of the outflow signal rather than a competing encoding.
 *  Named channels take ramp steps; the rolled-up remainder takes a muted
 *  neutral so it never competes with a real channel. */
export const CHANNEL_RAMP = [
  "#3F6B14",
  "#5C8228",
  "#6D9440",
  "#8A6300",
  "#96702A",
];
export const OTHER_CHANNEL_LABEL = "All other channels";
/** Darkened share track for use on the light-green sales node. */
export const SALES_NODE_TRACK = "#c4cfae";

export const channelColor = (
  channel: string,
  index: number,
  mutedNeutral: string
): string =>
  channel === OTHER_CHANNEL_LABEL
    ? mutedNeutral
    : CHANNEL_RAMP[index % CHANNEL_RAMP.length];

/** Negative magnitudes use the same typographic minus as every other
 *  signed figure on the page — never an ASCII hyphen. */
const signPrefix = (n: number): string => (n < 0 ? MINUS : "");

export const usd = (n: number, digits = 1): string => {
  const a = Math.abs(n);
  const sign = signPrefix(n);
  if (a >= 1e9) {
    return sign + "$" + (a / 1e9).toFixed(digits) + "B";
  }
  if (a >= 1e6) {
    return sign + "$" + (a / 1e6).toFixed(digits) + "M";
  }
  if (a >= 1e3) {
    // Guard the boundary: 999,600 must not render as "$1000K".
    const k = Math.round(a / 1e3);
    return k >= 1000
      ? sign + "$" + (a / 1e6).toFixed(digits) + "M"
      : sign + "$" + k + "K";
  }
  // Sub-$1000 values must not leak raw decimals ("$997.248").
  return (
    sign +
    "$" +
    a.toLocaleString("en-US", { maximumFractionDigits: 0 })
  );
};

export const un = (n: number): string => {
  const a = Math.abs(n);
  const sign = signPrefix(n);
  if (a >= 1e6) {
    return sign + (a / 1e6).toFixed(2) + "M";
  }
  if (a >= 1e3) {
    const k = Math.round(a / 1e3);
    return k >= 1000 ? sign + (a / 1e6).toFixed(2) + "M" : sign + k + "K";
  }
  return sign + a.toLocaleString("en-US", { maximumFractionDigits: 0 });
};

export const fmtValue = (v: number, unit: SnapshotUnit): string => {
  if (unit === "$") {
    return usd(v);
  }
  if (unit === "u") {
    return un(v);
  }
  if (unit === "%") {
    return v.toFixed(1) + "%";
  }
  return v.toFixed(1) + "w";
};

/** Formats a possibly-missing metric; missing renders as an em dash. */
export const fmtMetric = (m: SnapshotMetric): string =>
  m.current == null ? EM_DASH : fmtValue(m.current, m.unit);

/**
 * Delta vs the previous run. Renders nothing when there is no baseline.
 * `unitLabel` overrides the emitted suffix for point-unit deltas — the
 * supply figure switches between weeks and months, and the chip must carry
 * whichever unit the value beside it is displayed in.
 */
export const DeltaChip = ({
  current,
  prev,
  unit,
  unitLabel,
  good = "up",
  size = 11,
}: {
  current: number | null;
  prev: number | null;
  unit: SnapshotUnit;
  unitLabel?: string;
  good?: "up" | "down" | "none";
  size?: number;
}) => {
  const t = useSnapshotTokens();
  if (current == null || prev == null || prev === 0) {
    return null;
  }
  const pct = ((current - prev) / Math.abs(prev)) * 100;
  const up = pct >= 0;
  const healthy = good === "none" ? null : good === "up" ? up : !up;
  const color = healthy == null ? t.ink3 : healthy ? t.greenInk : t.redInk;
  // Point units (% / w) read better as absolute deltas than as % of a %.
  const isPointUnit = unit === "%" || unit === "w";
  const suffix = isPointUnit ? unitLabel ?? (unit === "%" ? "pt" : "w") : "%";
  const label = fmtSigned(isPointUnit ? current - prev : pct, 1, suffix);
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: size,
        fontWeight: 600,
        color,
        whiteSpace: "nowrap",
      }}
    >
      <svg
        width="8"
        height="8"
        viewBox="0 0 8 8"
        style={{ transform: up ? "none" : "rotate(180deg)" }}
      >
        <path d="M4 1L7 6H1z" fill="currentColor" />
      </svg>
      {label}
    </span>
  );
};

const ExternalLinkGlyph = ({ style }: { style?: React.CSSProperties }) => (
  <svg
    width="9"
    height="9"
    viewBox="0 0 10 10"
    style={{ opacity: 0.4, flexShrink: 0, ...style }}
  >
    <path
      d="M2.5 7.5L7.5 2.5M4 2.5h3.5V6"
      stroke="currentColor"
      strokeWidth="1.3"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

/**
 * A read-only value that deep-links into the view it came from. Underlines on
 * hover and carries a small external-link glyph. `to` is a route path; null
 * renders the children as plain (non-interactive) content.
 */
export const LinkVal = ({
  to,
  label,
  children,
  style,
}: {
  to: string | null;
  label: string;
  children: ReactNode;
  style?: React.CSSProperties;
}) => {
  const t = useSnapshotTokens();
  const { navigateWithGuards } = useGuardedNavigation();
  if (!to) {
    return <span style={style}>{children}</span>;
  }
  return (
    <span
      title={"Open " + label}
      role="link"
      tabIndex={0}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        cursor: "pointer",
        borderBottom: "1px solid transparent",
        ...style,
      }}
      onClick={() => navigateWithGuards(to)}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          navigateWithGuards(to);
        }
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderBottomColor = t.ink5;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderBottomColor = "transparent";
      }}
    >
      {children}
      <ExternalLinkGlyph />
    </span>
  );
};

/** Square = hub, circle = spoke/DC — the location marker used across rows. */
export const RoleDot = ({ role }: { role: "hub" | "spoke" | "dc" | null }) => {
  const t = useSnapshotTokens();
  if (!role) {
    return null;
  }
  return (
    <span
      style={{
        display: "inline-block",
        width: 6,
        height: 6,
        borderRadius: role === "hub" ? 1 : "50%",
        background: role === "hub" ? t.blue : t.ink4,
        flexShrink: 0,
      }}
    />
  );
};

export const SectionTitle = ({
  children,
  note,
  right,
}: {
  children: ReactNode;
  note?: string;
  right?: ReactNode;
}) => {
  const t = useSnapshotTokens();
  return (
    <div
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 10,
        marginBottom: 10,
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: 0.6,
          textTransform: "uppercase",
          color: t.ink2,
        }}
      >
        {children}
      </h3>
      {note && <span style={{ fontSize: 11.5, color: t.ink4 }}>{note}</span>}
      <div style={{ flex: 1 }} />
      {right}
    </div>
  );
};

export const SnapCard = ({
  children,
  style,
  pad = 16,
}: {
  children: ReactNode;
  style?: React.CSSProperties;
  pad?: number;
}) => {
  const t = useSnapshotTokens();
  return (
    <div
      style={{
        background: t.surface,
        border: `1px solid ${t.line}`,
        borderRadius: 10,
        padding: pad,
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export const Sparkline = ({
  values,
  w = 230,
  h = 44,
  color,
  fill = false,
  rangeFit = false,
}: {
  values: number[];
  w?: number;
  h?: number;
  color: string;
  fill?: boolean;
  /** Scale to the data's own range instead of anchoring at zero. An
   *  in-stock series moving 91→95% is a flat line when zero-anchored. */
  rangeFit?: boolean;
}) => {
  const t = useSnapshotTokens();
  if (!values.length) {
    return (
      <span
        title="No trend data available for this run"
        style={{ fontSize: 12, color: t.ink4 }}
      >
        {EM_DASH}
      </span>
    );
  }
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);
  // A hair of padding so the extremes don't sit exactly on the edge.
  const pad = rangeFit ? (dataMax - dataMin || 1) * 0.15 : 0;
  const minV = rangeFit ? dataMin - pad : Math.min(dataMin, 0);
  const maxV = rangeFit ? dataMax + pad : dataMax;
  const range = maxV - minV || 1;
  const step = w / (values.length - 1 || 1);
  const points = values.map((v, i) => [
    i * step,
    h - ((v - minV) / range) * (h - 4) - 2,
  ]);
  const path = points
    .map((p, i) => (i === 0 ? "M" : "L") + p[0].toFixed(1) + " " + p[1].toFixed(1))
    .join(" ");
  const area = path + ` L ${w} ${h} L 0 ${h} Z`;
  return (
    <svg width={w} height={h} style={{ display: "block", maxWidth: "100%" }}>
      {fill && <path d={area} fill={color} fillOpacity={0.12} />}
      <path
        d={path}
        stroke={color}
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {points.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r="1.6" fill={color} />
      ))}
    </svg>
  );
};
